import {
  LabelType,
  Options,
  NgxSliderModule,
} from '@angular-slider/ngx-slider';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { getFilterUnity } from '../../helpers/filter.helpers';

import { BrowseFilter, FilterNamesEnum } from '../../models/filter';
import { FilterOption } from '../../models/filter-options';
import { DistancePipe, SpeedPipe, Unit } from '@kinomap-dev/ng-ui-lib';

@Component({
  selector: 'app-filter-slider',
  templateUrl: './filter-slider.component.html',
  styleUrls: ['./filter-slider.component.scss'],
  standalone: true,
  imports: [NgxSliderModule],
})
export class FilterSliderComponent implements OnChanges {
  @Input() filter!: BrowseFilter;
  @Input() unit: Unit = 'metric';
  @Input() fullRangeOption!: FilterOption;
  @Input() selectedOptions!: FilterOption[];

  @Output() selectedOptionsChange: EventEmitter<FilterOption[]>;

  minValue!: number;
  maxValue!: number;
  sliderOptions!: Options;

  manualRefresh: EventEmitter<void>;
  distancePipe: DistancePipe = new DistancePipe();
  speedPipe: SpeedPipe = new SpeedPipe();

  get hasReachedCeil(): boolean {
    return this.maxValue === this.sliderOptions.ceil;
  }

  constructor() {
    this.selectedOptionsChange = new EventEmitter<FilterOption[]>();
    this.manualRefresh = new EventEmitter<void>();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!!changes.fullRangeOption) {
      this.setSliderOptions();
    }
    if (!this.selectedOptions?.length && this.fullRangeOption) {
      this.selectedOptions = [{ ...this.fullRangeOption }];
    }
    this.setSliderValues();
  }

  onSliderChange(): void {
    const valuesArray: number[] = [this.minValue, this.maxValue];
    const label: string = this.getOptionLabel();
    const value: string = valuesArray.join('-');

    const selectedOption: FilterOption = {
      ...this.fullRangeOption,
      label,
      value,
    };

    if (this.fullRangeOption?.isCustom) {
      selectedOption.isCustom = true;
    }

    this.selectedOptionsChange.emit([selectedOption]);
  }

  private setSliderOptions(): void {
    let filterUnity: string = getFilterUnity(this.filter.name);

    this.sliderOptions = {
      floor: this.getMinValue(this.fullRangeOption),
      ceil: this.getMaxValue(this.fullRangeOption),
      translate: (value: number, label: LabelType): string => {
        const key: string = this.filter.label.match(
          /filters\.([^.]+)\.title/
        )?.[1];

        switch (key) {
          case 'distance':
          case 'slope':
            filterUnity = this.distancePipe.transform(value, 'unit', this.unit);
            value = +this.distancePipe.transform(value, 'value', this.unit);
            break;
          case 'speed':
            filterUnity = this.speedPipe.transform(value, 'unit', this.unit);
            value = Math.round(
              +this.speedPipe.transform(value, 'value', this.unit)
            );
            break;
        }

        switch (label) {
          case LabelType.Ceil:
            return value + ` ${filterUnity} +`;
          case LabelType.High:
            return this.hasReachedCeil
              ? value + ` ${filterUnity} +`
              : value + ` ${filterUnity}`;
          default:
            return value + ` ${filterUnity}`;
        }
      },
    };
    this.emitManualRefresh();
  }

  private setSliderValues(): void {
    const referenceOption: FilterOption | undefined =
      this.selectedOptions?.[0] ?? this.fullRangeOption;

    if (!referenceOption) {
      return;
    }

    this.minValue = this.getMinValue(referenceOption);
    this.maxValue = this.getMaxValue(referenceOption);
  }

  private getMinValue(filterOption: FilterOption): number {
    return +filterOption.value.split('-')[0];
  }

  private getMaxValue(filterOption: FilterOption): number {
    return +filterOption.value.split('-')[1];
  }

  private getOptionLabel(): string {
    let label: string = 'browse.filterSort.filters.';

    let word: string = '';
    switch (this.filter.name) {
      case FilterNamesEnum.Distance:
        word = 'distance';
        break;
      case FilterNamesEnum.Slope:
        word = 'slope';
        break;
      case FilterNamesEnum.Speed:
        word = 'speed';
        break;
    }

    label += word;
    label += '.options.';

    if (this.minValue === 0 && this.hasReachedCeil) {
      label += 'between';
    } else {
      if (this.minValue === 0) {
        label += 'under';
      } else if (this.hasReachedCeil) {
        label += 'over';
      } else {
        label += 'between';
      }
    }

    return label;
  }

  // Fixes a known issue with ngx slider
  private emitManualRefresh(): void {
    setTimeout(() => {
      this.manualRefresh.emit();
    }, 300);
  }
}
